import { useEffect, useState } from "react";
import { ArrowUpRight, Sparkles } from "lucide-react";
import { api, API } from "@/lib/api";
import { track } from "@/lib/analytics";

const Card = ({ r, roadmapId, phaseIndex, disclosure }) => (
  <li className="panel p-5 hover:bg-white/[0.05] transition-colors duration-200" data-testid={`resource-card-${r.resource_id}`}>
    <div className="flex items-start gap-4">
      {r.image_url && (
        <img src={r.image_url} alt="" className="h-10 w-10 object-contain rounded-sm border border-white/10" />
      )}
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <p className="text-sm font-medium">{r.name}</p>
          {r.sponsored && (
            <span className="text-[10px] uppercase tracking-[0.16em] px-2 py-0.5 rounded-full border border-accent/40 text-accent" data-testid="sponsored-label">
              {r.sponsor_label || "Sponsored"}
            </span>
          )}
        </div>
        <p className="mt-1.5 text-sm text-muted-foreground leading-relaxed">{r.reason || r.description}</p>
        <div className="mt-2 flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
          {r.category && <span>{r.category}</span>}
          {r.price && <span>· {r.price}</span>}
        </div>
      </div>
    </div>
    <a
      href={`${API}/r/${r.resource_id}?roadmap_id=${roadmapId}&phase_index=${phaseIndex}`}
      target="_blank"
      rel="noopener noreferrer sponsored"
      onClick={() =>
        track("resource_clicked", {
          resource_id: r.resource_id,
          resource_name: r.name,
          category: r.category,
          is_affiliate: r.is_affiliate,
          sponsored: r.sponsored,
          phase_index: phaseIndex,
        })
      }
      data-testid={`view-resource-${r.resource_id}`}
      className="mt-4 inline-flex items-center gap-2 text-xs px-4 py-2 rounded-full border border-white/12 hover:bg-white/[0.07] hover:text-white transition-colors duration-200"
    >
      View Resource
      <ArrowUpRight className="h-3.5 w-3.5" />
    </a>
    {r.is_affiliate && (
      <p className="mt-3 text-[11px] text-muted-foreground/80 leading-relaxed" data-testid="affiliate-disclosure">
        {disclosure}
      </p>
    )}
  </li>
);

export const PhaseRecommendations = ({ roadmapId, phaseIndex }) => {
  const [data, setData] = useState(null);

  useEffect(() => {
    api
      .get(`/recommendations?roadmap_id=${roadmapId}&phase_index=${phaseIndex}`)
      .then(setData)
      .catch(() => {});
  }, [roadmapId, phaseIndex]);

  if (!data || (!data.resources.length && !data.sponsored.length)) return null;

  return (
    <div className="mt-7" data-testid={`phase-recommendations-${phaseIndex}`}>
      <span className="overline flex items-center gap-2">
        <Sparkles className="h-3.5 w-3.5" /> Recommended for this stage
      </span>
      <ul className="mt-4 space-y-3">
        {data.resources.map((r) => (
          <Card key={r.resource_id} r={r} roadmapId={roadmapId} phaseIndex={phaseIndex} disclosure={data.disclosure} />
        ))}
      </ul>
      {data.sponsored.length > 0 && (
        <ul className="mt-3 space-y-3" data-testid={`phase-sponsored-${phaseIndex}`}>
          {data.sponsored.map((r) => (
            <Card key={r.resource_id} r={r} roadmapId={roadmapId} phaseIndex={phaseIndex} disclosure={data.disclosure} />
          ))}
        </ul>
      )}
    </div>
  );
};
